import { DEFAULT_PPQ, ticksPerStep } from '../utils/noteEvents';

const finite = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;

function gridEvents(clipData, ppq) {
  const stepTicks = ticksPerStep(ppq);
  return Object.entries(clipData?.pattern || {}).flatMap(([key, active], index) => {
    if (!active) return [];
    const parts = String(key).split('-');
    if (parts.length !== 2) return [];
    const drumIndex = Number(parts[0]);
    const step = Number(parts[1]);
    if (!Number.isInteger(drumIndex) || drumIndex < 0 || !Number.isInteger(step) || step < 0) return [];
    const info = clipData?.noteData?.[key] || {};
    const pitches = Array.isArray(info.pitches) && info.pitches.length
      ? info.pitches : [finite(info.pitch, 72)];
    return [{
      id: `grid-${index}`,
      drumIndex,
      startTick: Math.round(step * stepTicks),
      durationTicks: Math.max(1, Math.round(finite(info.durationTicks, finite(info.durationSteps, 1) * stepTicks))),
      pitch: finite(info.pitch, pitches[0] ?? 72),
      pitches,
      velocity: finite(info.velocity, 1)
    }];
  });
}

function eventsToGrid(events, ppq) {
  const stepTicks = ticksPerStep(ppq);
  const pattern = {};
  const noteData = {};
  events.forEach((event) => {
    const drumIndex = Number(event?.drumIndex);
    if (!Number.isInteger(drumIndex) || drumIndex < 0) return;
    const step = Math.max(0, Math.floor(finite(event.startTick) / stepTicks));
    const key = `${drumIndex}-${step}`;
    const existing = noteData[key] || {};
    const pitches = [...new Set([
      ...(Array.isArray(existing.pitches) ? existing.pitches : []),
      ...(Number.isFinite(existing.pitch) ? [existing.pitch] : []),
      ...(Array.isArray(event.pitches) ? event.pitches : [event.pitch])
    ].map(Number).filter(Number.isFinite))].sort((a, b) => a - b);
    pattern[key] = true;
    noteData[key] = {
      pitch: pitches[0] ?? 72,
      ...(pitches.length > 1 ? { pitches } : {}),
      velocity: Math.max(finite(existing.velocity), finite(event.velocity, 1)),
      durationTicks: Math.max(finite(existing.durationTicks), Math.max(1, finite(event.durationTicks, stepTicks))),
      durationSteps: Math.max(finite(existing.durationSteps), Math.ceil(Math.max(1, finite(event.durationTicks, stepTicks)) / stepTicks)),
      ppq
    };
  });
  return { pattern, noteData };
}

function clipHalf(clipData, events, ppq, durationTicks, side) {
  const { pattern, noteData } = eventsToGrid(events, ppq);
  return {
    ...clipData,
    id: `${clipData?.id || 'pattern'}-${side}`,
    pattern,
    noteData,
    noteEvents: events,
    patternLength: Math.max(1, Math.ceil(durationTicks / ticksPerStep(ppq)))
  };
}

/** Split and rebase pattern events so each timeline half contains only its own music. */
export function splitPatternClipData(clipData = {}, splitTicks, totalTicks, options = {}) {
  const ppq = finite(clipData.ppq, DEFAULT_PPQ) || DEFAULT_PPQ;
  const drumPattern = options.drumPattern === true || clipData.kind !== 'instrument';
  const total = Math.max(1, Math.round(finite(totalTicks, finite(clipData.patternLength, 16) * ticksPerStep(ppq))));
  const split = Math.max(1, Math.min(total - 1, Math.round(finite(splitTicks, total / 2))));
  const sourceEvents = Array.isArray(clipData.noteEvents) && clipData.noteEvents.length
    ? clipData.noteEvents : gridEvents(clipData, ppq);
  const left = [];
  const right = [];

  sourceEvents.forEach((event, index) => {
    const start = Math.max(0, finite(event?.startTick));
    const end = Math.min(total, start + Math.max(1, finite(event?.durationTicks, ticksPerStep(ppq))));
    // Drum hits are point events. A hit crossing the cut belongs to the side
    // where it starts; duplicating it into both clips creates an extra hit on
    // the next playback pass. Sustained instrument notes keep the existing
    // split-and-truncate behavior below.
    if (drumPattern) {
      if (start < split && start < total) {
        left.push({
          ...event,
          id: `${event?.id || `event-${index}`}-L`,
          startTick: start,
          durationTicks: Math.max(1, Math.min(end, split) - start)
        });
      } else if (start >= split && start < total) {
        right.push({
          ...event,
          id: `${event?.id || `event-${index}`}-R`,
          startTick: start - split,
          durationTicks: Math.max(1, end - start)
        });
      }
      return;
    }
    if (start < split && end > 0) {
      left.push({
        ...event,
        id: `${event?.id || `event-${index}`}-L`,
        startTick: start,
        durationTicks: Math.max(1, Math.min(end, split) - start)
      });
    }
    if (end > split && start < total) {
      const rightStart = Math.max(start, split);
      right.push({
        ...event,
        id: `${event?.id || `event-${index}`}-R`,
        startTick: rightStart - split,
        durationTicks: Math.max(1, end - rightStart)
      });
    }
  });

  return {
    left: clipHalf(clipData, left, ppq, split, 'L'),
    right: clipHalf(clipData, right, ppq, total - split, 'R')
  };
}

export default splitPatternClipData;
